import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Button, Form, Pagination } from 'react-bootstrap';
import { default as Selector } from 'react-select';
import { toast } from 'react-toastify';
import { FaFileExcel } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { customStyles } from '~/Styles/reactSelectStyles';
import { exportarEmExcel } from '~/Utils/Helpers';
import {
  buscarEmpreendimentosEtiquetasImoveis,
  listarEtiquetasImoveis,
} from '~/Services/Service';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';

// W-0750 — Imob > Relatórios > Administração > Etiquetas Imóveis. Read-only.
// Fonte: ERP/ModuloImob/UEtqImoveis.pas. Uma query, duas renderizações:
//  - Etiqueta (preview, default): 5 campos SEM PII (espelha UqrDescricao).
//  - Excel: 15 colunas (A–O do GeraExcel) COM PII — export client-side via Helpers.
// RN-09: tela sem RBAC; menu gateado por char 630 (MigracaoERP — portão de migração HML).
// [DECISÃO OPERADOR LGPD] export Excel sem gate (replica-first). Sinalizado no retorno.

const ITENS_POR_PAGINA = 20;

// Colunas do Excel (A–O do GeraExcel do .pas), nesta ordem.
const COLUNAS_EXCEL = [
  { campo: 'codigo_ativo', titulo: 'Codigo_Ativo', tipo: 'texto' },
  { campo: 'mapa1', titulo: 'Quadra', tipo: 'texto' },
  { campo: 'mapa2', titulo: 'Lote', tipo: 'texto' },
  { campo: 'email', titulo: 'Email', tipo: 'texto' },
  { campo: 'fone_1', titulo: 'Fone_1', tipo: 'texto' },
  { campo: 'fone_2', titulo: 'Fone_2', tipo: 'texto' },
  { campo: 'fone_3', titulo: 'Fone_3', tipo: 'texto' },
  { campo: 'celular', titulo: 'Celular', tipo: 'texto' },
  { campo: 'area_total', titulo: 'M2', tipo: 'decimal' },
  { campo: 'matricula', titulo: 'Matrícula', tipo: 'texto' },
  { campo: 'cad_prefeitura', titulo: 'Cad. Prefeitura', tipo: 'texto' },
  { campo: 'cad_agua', titulo: 'Cad. Água', tipo: 'texto' },
  { campo: 'cartorio', titulo: 'Cartório', tipo: 'texto' },
  { campo: 'cidade_cartorio', titulo: 'Cidade Cartório', tipo: 'cidade' },
  { campo: 'desc_imovel', titulo: 'Desc_Imovel', tipo: 'texto' },
];

const formatarDecimal = valor => {
  if (valor === null || valor === undefined || valor === '') return '0,00';
  return Number(valor).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Cidade Cartório = Descricao_Cidade + ' - ' + Estado (col N do GeraExcel).
const formatarCidadeCartorio = item => {
  const cidade = item.cidade_cartorio ? item.cidade_cartorio : '';
  const uf = item.uf_cartorio ? item.uf_cartorio : '';
  if (cidade && uf) return `${cidade} - ${uf}`;
  if (cidade) return cidade;
  return uf;
};

// Desc_Imovel é memo livre do ERP (UEtqImoveis.pas) e traz CR/LF embutidos.
// No Excel isso liga o "quebrar texto" e infla a linha; CR isolado pode até
// quebrar o registro em várias linhas. Colapsa qualquer quebra num espaço.
const sanitizarTexto = valor =>
  String(valor)
    .replace(/[\r\n]+/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .trim();

const valorCelulaExcel = (item, coluna) => {
  if (coluna.campo === 'cidade_cartorio') return formatarCidadeCartorio(item);
  if (coluna.tipo === 'decimal') return formatarDecimal(item[coluna.campo]);
  const valor = item[coluna.campo];
  if (valor === null || valor === undefined) return '';
  return coluna.tipo === 'texto' ? sanitizarTexto(valor) : valor;
};

const EtiquetasImoveis = () => {
  const [empreendimentosOptions, setEmpreendimentosOptions] = useState([]);
  const [empreendimentoSelecionado, setEmpreendimentoSelecionado] = useState(null);
  const [apenasDisponiveis, setApenasDisponiveis] = useState(true);
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [carregouUmaVez, setCarregouUmaVez] = useState(false);
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    carregarEmpreendimentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarEmpreendimentos = async () => {
    try {
      const res = await buscarEmpreendimentosEtiquetasImoveis();
      const lista = res ? res : [];
      setEmpreendimentosOptions(
        lista.map(e => ({
          value: e.codigo_familia,
          label: `${e.codigo_familia} - ${e.nome_da_familia}`,
        }))
      );
    } catch (err) {
      toast.error(
        err && err.message ? err.message : 'Erro ao carregar empreendimentos.'
      );
    }
  };

  const gerar = async () => {
    // RN-01: empreendimento obrigatório.
    if (!empreendimentoSelecionado) {
      toast.warn('Selecione um empreendimento.');
      return;
    }
    try {
      setCarregando(true);
      const res = await listarEtiquetasImoveis(
        empreendimentoSelecionado.value,
        apenasDisponiveis
      );
      setRegistros(res ? res : []);
      setPagina(1);
      setCarregouUmaVez(true);
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro inesperado');
    } finally {
      setCarregando(false);
    }
  };

  const exportarExcel = () => {
    if (!registros || registros.length === 0) return;
    const cabecalho = COLUNAS_EXCEL.map(c => c.titulo);
    const linhas = registros.map(item =>
      COLUNAS_EXCEL.map(c => valorCelulaExcel(item, c))
    );
    exportarEmExcel([cabecalho, ...linhas], 'etiquetas-imoveis.csv');
  };

  // Paginação client-side das etiquetas (preview).
  const totalPaginas = Math.ceil(registros.length / ITENS_POR_PAGINA) || 1;
  const registrosPaginados = registros.slice(
    (pagina - 1) * ITENS_POR_PAGINA,
    pagina * ITENS_POR_PAGINA
  );

  const renderizarPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const itens = [];
    const maxBotoes = 5;

    itens.push(
      <Pagination.First
        key="first"
        onClick={() => setPagina(1)}
        disabled={pagina === 1}
      />
    );
    itens.push(
      <Pagination.Prev
        key="prev"
        onClick={() => setPagina(pagina - 1)}
        disabled={pagina === 1}
      />
    );

    let inicioPaginas = Math.max(1, pagina - Math.floor(maxBotoes / 2));
    const fimPaginas = Math.min(totalPaginas, inicioPaginas + maxBotoes - 1);
    if (fimPaginas - inicioPaginas < maxBotoes - 1) {
      inicioPaginas = Math.max(1, fimPaginas - maxBotoes + 1);
    }

    if (inicioPaginas > 1) {
      itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (let i = inicioPaginas; i <= fimPaginas; i += 1) {
      itens.push(
        <Pagination.Item
          key={i}
          active={i === pagina}
          onClick={() => setPagina(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (fimPaginas < totalPaginas) {
      itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    itens.push(
      <Pagination.Next
        key="next"
        onClick={() => setPagina(pagina + 1)}
        disabled={pagina === totalPaginas}
      />
    );
    itens.push(
      <Pagination.Last
        key="last"
        onClick={() => setPagina(totalPaginas)}
        disabled={pagina === totalPaginas}
      />
    );

    return (
      <div
        className="d-flex justify-content-between align-items-center px-3 py-2"
        style={{ borderTop: '1px solid #dee2e6' }}
      >
        <small className="text-muted">
          {`Mostrando ${
            registrosPaginados.length > 0
              ? (pagina - 1) * ITENS_POR_PAGINA + 1
              : 0
          } até ${Math.min(
            pagina * ITENS_POR_PAGINA,
            registros.length
          )} de ${registros.length} registros`}
        </small>
        <Pagination className="mb-0">{itens}</Pagination>
      </div>
    );
  };

  // Modo Etiqueta (preview): 5 campos NÃO-PII, formato espelha UqrDescricao.
  const renderizarEtiqueta = item => (
    <div>
      <div className="font-weight-bold">
        {`Imóvel: ${item.codigo_ativo} - ${item.nome ? item.nome : ''}`}
      </div>
      <div>
        {`Quadra: ${item.mapa1 ? item.mapa1 : ''} Lote: ${
          item.mapa2 ? item.mapa2 : ''
        }`}
      </div>
      {item.desc_imovel ? <div>{item.desc_imovel}</div> : null}
    </div>
  );

  return (
    <div>
      <Header title="Etiquetas Imóveis" />
      <Container fluid className="mt-3">
        <Card className="mb-3">
          <Card.Body>
            <Row className="align-items-end">
              <Col xs={12} md={6} lg={5}>
                <Form.Group className="mb-0">
                  <Form.Label>Empreendimento</Form.Label>
                  <Selector
                    isClearable
                    styles={customStyles}
                    placeholder="Selecione o empreendimento..."
                    noOptionsMessage={() => 'Nenhum empreendimento encontrado'}
                    options={empreendimentosOptions}
                    value={empreendimentoSelecionado}
                    onChange={selecionado =>
                      setEmpreendimentoSelecionado(selecionado || null)
                    }
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6} lg={4} className="mt-3 mt-md-0">
                <Form.Label className="d-block">Disponibilidade</Form.Label>
                <Form.Check
                  inline
                  type="radio"
                  id="disp-disponiveis"
                  name="disponibilidade"
                  label="Disponíveis"
                  checked={apenasDisponiveis === true}
                  onChange={() => setApenasDisponiveis(true)}
                />
                <Form.Check
                  inline
                  type="radio"
                  id="disp-todos"
                  name="disponibilidade"
                  label="Todos"
                  checked={apenasDisponiveis === false}
                  onChange={() => setApenasDisponiveis(false)}
                />
              </Col>
              <Col xs={12} md={12} lg={3} className="text-md-right mt-3 mt-lg-0">
                <Button
                  variant="primary"
                  className="mr-2"
                  disabled={carregando || !empreendimentoSelecionado}
                  onClick={gerar}
                >
                  Gerar
                </Button>
                <Button
                  variant="outline-success"
                  disabled={carregando || registros.length === 0}
                  onClick={exportarExcel}
                >
                  <FaFileExcel />
                  <span className="ml-2">Exportar Excel</span>
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {carregando && <Loading />}

        {!carregando && carregouUmaVez && (
          <Card>
            <Card.Body className="table-responsive">
              {registros.length === 0 && (
                <div className="text-center text-muted py-3">
                  Nenhum registro encontrado
                </div>
              )}
              {registros.length > 0 && (
                <>
                  <div className="px-3 pt-2">
                    <span className="font-weight-bold">
                      Total de imóveis - {registros.length}
                    </span>
                  </div>
                  <Table className="table table-striped table-bordered table-sm">
                    <Thead>
                      <Tr className="text-center">
                        <Th>Etiqueta</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {registrosPaginados.map((item, indice) => (
                        <Tr
                          key={`${(pagina - 1) * ITENS_POR_PAGINA + indice}-${
                            item.codigo_ativo
                          }`}
                        >
                          <Td>{renderizarEtiqueta(item)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {renderizarPaginacao()}
                </>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default EtiquetasImoveis;
